import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import {
  StripeCardCvcElementChangeEvent,
  StripeCardExpiryElementChangeEvent,
  StripeCardNumberElementChangeEvent,
} from "@stripe/stripe-js";
import { CardCvcElement, CardExpiryElement, CardNumberElement, useElements, useStripe } from "@stripe/react-stripe-js";

import { StatusCode } from "api/protocols";
import { PostAccountTransaction, PutAccountTransactionCapture } from "api/rpc/2024-04/facilityAdmin/customer/customer";

import { dequeue, enqueue, showError, showSuccess } from "redux/actions/ui";

import { useAppDispatch, useAppSelector } from "hooks/redux";
import { useStripeTerminalPayment } from "hooks/useStripeTerminalPayment/useStripeTerminalPayment";
import {
  clearReaderDisplay,
  sheetInfo,
  processAccountPayment as stripeProcessPayment,
} from "helpers/StripeTerminalWrapper";

import Input from "components/form/input/Input";
import Sheet from "components/sheet/Sheet";
import { ButtonNew as Button } from "components/buttonNew";
import Popup from "components/popup/Popup";
import CheckoutForm, { cancelManualCreditPaymentIntent } from "components/checkoutForm/CheckoutForm";
import Form from "components/form/Form";
import FormLayout from "components/form/FormLayout";
import TextField from "components/form/textField/TextField";

import { IStripePaymentState } from "pages/secure/facility/teesheet/TeeSheet";
import PaymentMethods from "elements/register/cartMenu/PaymentMethods";
import { HouseAccountType } from "../../context/contextTypes";
import CreditCardCatalogue from "elements/teesheet/creditCard/CreditCardCatalogue";
import { ICreditCard } from "pages/guest/Customer/CustomerProfile";
import CreditBookModal from "components/creditBookModal/CreditBookModal";
import Portal from "elements/Portal";
import { IPrizeAccount } from "pages/secure/facility/settings/prizeAccounts/PrizeAccount";

interface IAccountPaymentState {
  paymentAmount: string;
  paymentOption: Record<string, any>;
  transaction: Record<string, any>;
  clientSecret: string;
  manualCardModalVisible: boolean;
  elementComplete: {
    cardNumber: boolean;
    cardExpiry: boolean;
    cardCvc: boolean;
  };
  giftCardModalVisible: boolean;
  giftCardCode: string;
  giftCardPin: string;
  selectedCustomerPaymentMethod: number;
  showCustomerPaymentMethods: boolean;
  note: string;
  creditBookModalVisible: boolean;
  selectedCreditBook: IPrizeAccount;
}

type HouseAccountPaymentModalProps = {
  paymentModalVisible: boolean;
  houseAccount: HouseAccountType;
  paymentMethods: ICreditCard[];
  onOk: () => void;
  /** Closes the base payment modal. */
  closePaymentModal?: () => void;
};

export default function HouseAccountPaymentModal(props: HouseAccountPaymentModalProps) {
  const stripe = useStripe();
  const elements = useElements();
  const { processPayment: processTerminalPayment, StripeTerminalSheet, paymentStatus } = useStripeTerminalPayment();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { facilityStore, terminalStore } = useAppSelector(store => store);

  const { houseAccount } = props;

  const [manualProcessing, setManualProcessing] = useState<boolean>(false);
  const [submitTrigger, setSubmitTrigger] = useState<boolean>(false);
  const [paymentInProgress, setPaymentInProgress] = useState<boolean>(false);

  const [paymentState, setPaymentState] = useState<IAccountPaymentState>({
    paymentAmount: props.houseAccount.balance.toString(),
    paymentOption: null,
    transaction: undefined,
    clientSecret: "",
    manualCardModalVisible: false,
    elementComplete: {
      cardNumber: false,
      cardExpiry: false,
      cardCvc: false,
    },
    giftCardModalVisible: false,
    giftCardCode: "",
    giftCardPin: "",
    selectedCustomerPaymentMethod: undefined,
    showCustomerPaymentMethods: false,
    note: "",
    creditBookModalVisible: false,
    selectedCreditBook: null,
  });

  const [stripeState, setStripeState] = useState<IStripePaymentState>({
    onCancel: () => { },
    onOk: () => { },
    cancelText: "",
    okText: "",
    sheetVisible: false,
    titleText: "",
    message: "",
    convertedCode: "",
    processing: false,
    success: false,
  });

  const elementsComplete =
    paymentState.elementComplete.cardNumber === true &&
    paymentState.elementComplete.cardExpiry === true &&
    paymentState.elementComplete.cardCvc === true;

  // refresh parent once terminal paymentStatus is completed
  useEffect(() => {
    if (paymentStatus === "completed") {
      props.onOk();
      props.closePaymentModal();
    }
  }, [paymentStatus]);

  useEffect(() => {
    console.log("paymentState.selectedCreditBook", paymentState.selectedCreditBook);
    if (paymentState.selectedCreditBook) {
      void processPayment();
    }
  }, [paymentState.selectedCreditBook]);

  async function processPayment() {
    dispatch(enqueue());
    setPaymentInProgress(true);
    try {
      if (
        paymentState.paymentAmount === "" ||
        !Number(paymentState.paymentAmount) ||
        Number.isNaN(paymentState.paymentAmount)
      ) {
        setPaymentInProgress(false);
        dispatch(showError(`'${paymentState.paymentAmount}' is not a valid amount`));
        return;
      }

      if (paymentState.paymentOption?.payment_method === "manual_card") {
        console.log(paymentState);

        const register = JSON.parse(localStorage.getItem("register"));
        const registerId = register?.id;

        const transactionRes = await PostAccountTransaction(
          {
            kind: "account_payment",
            amount: Number(paymentState.paymentAmount),
            account_id: houseAccount.id,
            payment_option_id: paymentState.paymentOption?.id,
            note: paymentState.note,
            register_id: registerId,
          },
          true,
        );

        if (Number(transactionRes.status) === Number(StatusCode.OK)) {
          setPaymentState(prevState => ({
            ...prevState,
            transaction: transactionRes.data,
            clientSecret: transactionRes.data.client_secret,
            manualCardModalVisible: true,
            paymentModalVisible: false,
          }));
        } else {
          setPaymentInProgress(false);
          dispatch(showError(t("Account Transaction Failed")));
          dispatch(dequeue());
          return;
        }
      } else if (paymentState.paymentOption?.payment_method === "card") {
        if (facilityStore.facility.terminal_integration === "server") {
          setPaymentState(prevState => ({ ...prevState, paymentModalVisible: false }));
          await processTerminalPayment(
            houseAccount.id,
            paymentState.paymentOption,
            Number(paymentState.paymentAmount),
            true,
            false,
            null,
            null,
            null,
            true,
          );
        } else {
          ReactDOM.unstable_batchedUpdates(() => {
            setPaymentState(prevState => ({ ...prevState, paymentModalVisible: false }));

            setStripeState(prevState => ({
              ...prevState,
              sheetVisible: true,
              onCancel: undefined,
              okText: t("Cancel Payment"),
              processing: true,
              onOk: async () => {
                setStripeState(prevState => ({
                  ...prevState,
                  sheetVisible: false,
                  onCancel: undefined,
                  okText: undefined,
                  processing: false,
                  onOk: () => { },
                  titleText: "",
                  success: false,
                  message: "",
                }));
                await clearReaderDisplay();
              },
              titleText: t("Processing payment"),
              message: t("Please wait while the customer finishes the transaction…"),
            }));
          });

          await stripeProcessPayment(
            houseAccount.id,
            Number(paymentState.paymentAmount),
            paymentState.paymentOption,
            handleStripeSuccess,
            handleStripeError,
          );
        }
      } else if (paymentState.paymentOption?.payment_method === "gift_card") {
        const register = JSON.parse(localStorage.getItem("register"));
        const registerId = register?.id;

        const transactionRes = await PostAccountTransaction(
          {
            kind: "account_payment",
            amount: Number(paymentState.paymentAmount),
            account_id: houseAccount.id,
            payment_option_id: paymentState.paymentOption?.id,
            gift_card_code: paymentState.giftCardCode,
            gift_card_pin: paymentState.giftCardPin,
            note: paymentState.note,
            register_id: registerId,
          },
          true,
        );

        if (Number(transactionRes.status) === Number(StatusCode.OK)) {
          setPaymentState(prevState => ({
            ...prevState,
            giftCardModalVisible: false,
            giftCardCode: null,
            giftCardPin: null,
          }));

          dispatch(showSuccess(t("Account Transaction Succeeded")));
          props.onOk();
          props.closePaymentModal();
        }
      } else if (paymentState.paymentOption?.payment_method === "credit_book") {
        if (!paymentState.selectedCreditBook) {
          dispatch(showError(t("No credit book selected")));
          return;
        }

        const register = JSON.parse(localStorage.getItem("register"));
        const registerId = register?.id;

        const transactionRes = await PostAccountTransaction(
          {
            kind: "account_payment",
            amount: Number(paymentState.paymentAmount),
            account_id: houseAccount.id,
            payment_option_id: paymentState.paymentOption?.id,
            credit_book_id: paymentState.selectedCreditBook?.id,
            note: paymentState.note,
            register_id: registerId,
          },
          true,
        );

        if (transactionRes.status === StatusCode.OK) {
          setPaymentState(prevState => ({
            ...prevState,
            creditBookModalVisible: false,
            selectedCreditBook: null,
          }));

          dispatch(showSuccess(t("Account Transaction Succeeded")));
          props.onOk();
          props.closePaymentModal();
        } else {
          setPaymentState(prevState => ({
            ...prevState,
            creditBookModalVisible: false,
            selectedCreditBook: null,
          }));

          dispatch(showError(t("Account Transaction Failed")));
        }
      } else {
        //Cash & Moneris payments
        const register = JSON.parse(localStorage.getItem("register"));
        const registerId = register?.id;

        const transactionRes = await PostAccountTransaction(
          {
            kind: "account_payment",
            amount: Number(paymentState.paymentAmount),
            account_id: houseAccount.id,
            payment_option_id: paymentState.paymentOption?.id,
            note: paymentState.note,
            register_id: registerId,
          },
          true,
        );

        if (Number(transactionRes.status) !== Number(StatusCode.OK)) {
          setPaymentInProgress(false);
          dispatch(showError(t("Account Transaction Failed")));
          dispatch(dequeue());
          return;
        }
        dispatch(showSuccess(t("Account Transaction Succeeded")));
        props.onOk();
        props.closePaymentModal();
      }
    } catch (e) {
      console.log(e);
      dispatch(showError(t("Error processing payment. Please try again.")));
    }
    setPaymentInProgress(false);
    dispatch(dequeue());
  }

  async function handleProcessCustomerCard() {
    if (!stripe) {
      console.log("Stripe setup error");
      return;
    }

    const stripePaymentMethod = props.paymentMethods.find(
      method => method.id === paymentState.selectedCustomerPaymentMethod,
    );

    dispatch(enqueue());
    const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(paymentState.clientSecret, {
      payment_method: stripePaymentMethod.stripe_payment_method_id,
    });

    if (stripeError) {
      dispatch(showError(stripeError.message));
      dispatch(dequeue());
      return;
    }

    //Capture the payment
    const captureRes = await PutAccountTransactionCapture(
      { payment_intent_id: paymentIntent.id, transaction_id: paymentState.transaction?.id },
      false,
    );

    if (captureRes.status !== StatusCode.OK) {
      dispatch(showError(captureRes.message));
      dispatch(dequeue());
      return;
    }

    dispatch(showSuccess(captureRes.message));
    setPaymentState(prevState => ({
      ...prevState,
      showCustomerPaymentMethods: false,
      selectedCustomerPaymentMethod: undefined,
      manualCardModalVisible: false,
      transaction: undefined,
      clientSecret: "",
      paymentAmount: houseAccount.balance.toString(),
      elementComplete: {
        cardCvc: false,
        cardExpiry: false,
        cardNumber: false,
      },
    }));

    dispatch(dequeue());
    props.onOk();
    props.closePaymentModal();
  }

  function handleStripeSuccess() {
    setStripeState(prevState => ({
      ...prevState,
      sheetVisible: true,
      onCancel: undefined,
      okText: t("Close"),
      onOk: () => {
        setStripeState(prevState => ({
          ...prevState,
          sheetVisible: false,
          onCancel: undefined,
          okText: undefined,
          processing: false,
          onOk: () => { },
          titleText: "",
          message: "",
        }));
      },
      processing: false,
      success: true,
      titleText: t("Payment processed"),
      message: t("Payment successful"),
    }));

    setPaymentState({ ...paymentState, paymentAmount: "", paymentOption: null });
    dispatch(showSuccess(t("Payment successful")));

    props.onOk();
    props.closePaymentModal(); // void loadHouseAccounts(true);
  }

  function handleStripeError(message: string, sheetInfo: sheetInfo) {
    //set sheet
    setStripeState(prevState => ({
      ...prevState,
      onCancel: () => {
        sheetInfo.onCancel();
        setStripeState(prevState => ({ ...prevState, sheetVisible: false }));
      },
      onOk: () => {
        sheetInfo.onOk();
        setStripeState(prevState => ({
          ...prevState,
          sheetVisible: true,
          onCancel: undefined,
          okText: "Cancel Payment",
          processing: true,
          onOk: async () => {
            setStripeState(prevState => ({
              ...prevState,
              sheetVisible: false,
              onCancel: undefined,
              okText: undefined,
              processing: false,
              onOk: () => { },
              titleText: "",
              success: false,
              message: "",
            }));
            await clearReaderDisplay();
          },
          titleText: t("Processing payment"),
          message: t("Please wait while the customer finishes the transaction…"),
        }));
      },
      cancelText: sheetInfo.cancelText,
      okText: sheetInfo.okText,
      sheetVisible: true,
      titleText: sheetInfo.title,
      message: sheetInfo.message,
      convertedCode: sheetInfo.convertedErrorCode,
      success: false,
      processing: false,
    }));
  }

  // completing stripe card inputs auto-submits the onOk.  This handles the early call on that page
  function handleManualCardProcessing() {
    if (paymentState.selectedCustomerPaymentMethod) {
      void handleProcessCustomerCard();
    } else if (elementsComplete && !submitTrigger) {
      setSubmitTrigger(!submitTrigger);
    }
  }

  function handleCardSectionChange(
    e: StripeCardNumberElementChangeEvent | StripeCardExpiryElementChangeEvent | StripeCardCvcElementChangeEvent,
  ) {
    setPaymentState(prevState => ({
      ...prevState,
      elementComplete: { ...prevState.elementComplete, [e.elementType]: e.complete },
      selectedCustomerPaymentMethod: undefined,
      showCustomerPaymentMethods: false,
    }));
  }

  return (
    <>
      <StripeTerminalSheet />

      {/* Base Sheet */}
      {props.paymentModalVisible && (
        <Sheet
          open={props.paymentModalVisible}
          size="small"
          height="flexible"
          closable
          title={t("Account Payment")}
          onCancel={() => props.closePaymentModal()}
          onOk={
            paymentState.paymentOption?.payment_method === "gift_card"
              ? () => setPaymentState({ ...paymentState, giftCardModalVisible: true })
              : paymentState.paymentOption?.payment_method === "credit_book"
                ? () => setPaymentState({ ...paymentState, creditBookModalVisible: true })
                : processPayment
          }
          cancelText={t("Cancel")}
          okText={
            paymentState.paymentOption?.payment_method === "gift_card"
              ? t("Enter Gift Card")
              : paymentState.paymentOption?.payment_method === "credit_book"
                ? t("Select Credit Book")
                : t("Process Payment")
          }
          backDropCancel={false}
          okDisabled={paymentState.paymentOption === null || Number(paymentState.paymentAmount) <= 0 ? true : false}
          okLoading={paymentInProgress}
        >
          <Form>
            <FormLayout>
              <FormLayout.Group>
                <PaymentMethods
                  onChange={paymentOption =>
                    setPaymentState(prevState => ({
                      ...prevState,
                      paymentOption: paymentOption !== undefined ? paymentOption : null,
                    }))
                  }
                  paymentOptions={facilityStore.paymentOptions.filter(
                    paymentOption =>
                      paymentOption.payment_method !== "room_charge_rdp" && paymentOption.payment_method !== "maestro" && paymentOption?.payment_method !== "deposit",
                  )}
                  cardReaderAvailable={terminalStore?.reader === null ? false : true}
                  cart={undefined}
                  paymentAmount={Number(paymentState.paymentAmount)}
                  value={paymentState.paymentOption}
                />
              </FormLayout.Group>

              <FormLayout.Group>
                <TextField
                  label={t("Notes")}
                  name={t("Note")}
                  id="note"
                  onChange={({ target }: { target: EventTarget & HTMLTextAreaElement }) =>
                    setPaymentState({ ...paymentState, [target.id]: target.value })
                  }
                  value={paymentState.note}
                  rows={2}
                  maxLength={255}
                />
              </FormLayout.Group>

              <FormLayout.Group>
                <Input
                  prefix="$"
                  type="number"
                  label={t("Amount")}
                  id="paymentAmount"
                  value={paymentState.paymentAmount}
                  onChange={({ target }: { target: EventTarget & HTMLInputElement }) =>
                    setPaymentState({ ...paymentState, paymentAmount: target.value })
                  }
                />
              </FormLayout.Group>
            </FormLayout>
          </Form>
        </Sheet>
      )}

      <>
        {stripeState.processing ? (
          <Popup
            type="info"
            open={stripeState.sheetVisible}
            title={t("Please wait..")}
            description={t("Customer is completing the transaction")}
            closable
            onCancel={stripeState.onCancel}
            onOk={stripeState.onOk}
            cancelText={stripeState.cancelText}
            okText={stripeState.okText}
            backDropCancel={false}
          />
        ) : (
          <>
            {stripeState.success ? (
              <Popup
                type="success"
                open={stripeState.sheetVisible}
                title={t("Payment Complete")}
                description={t("Customer has successfully completed their transaction")}
                closable
                onCancel={stripeState.onCancel}
                onOk={stripeState.onOk}
                cancelText={stripeState.cancelText}
                okText={stripeState.okText}
                backDropCancel={false}
              />
            ) : (
              <Popup
                type="error"
                open={stripeState.sheetVisible}
                title={t("Payment Error")}
                description={stripeState.message + " - Error Code: " + stripeState.convertedCode}
                closable
                onCancel={stripeState.onCancel}
                onOk={stripeState.onOk}
                cancelText={stripeState.cancelText}
                okText={stripeState.okText}
                backDropCancel={false}
              />
            )}
          </>
        )}
      </>

      {/* GIFT CARD */}
      <Sheet
        title={t("Enter Gift Card")}
        open={paymentState.giftCardModalVisible}
        size="small"
        closable
        stacked
        backDropCancel={false}
        onCancel={() =>
          setPaymentState(prevState => ({
            ...prevState,
            giftCardModalVisible: false,
            giftCardCode: "",
            giftCardPin: "",
          }))
        }
        onOk={processPayment}
        okText={t("Use Gift Card")}
        cancelText={t("Back")}
        okDisabled={paymentState.giftCardCode === "" && paymentState.giftCardPin ? true : false}
      >
        <Input
          value={paymentState.giftCardCode}
          placeholder={t("Gift card code")}
          id="giftCardCode"
          onChange={({ target }: { target: EventTarget & HTMLInputElement }) =>
            setPaymentState({ ...paymentState, [target.id]: target.value })
          }
        />
        <Input
          value={paymentState.giftCardPin}
          placeholder={t("Gift card pin")}
          id="giftCardPin"
          onChange={({ target }: { target: EventTarget & HTMLInputElement }) =>
            setPaymentState({ ...paymentState, [target.id]: target.value })
          }
        />
      </Sheet>

      {/* MANUAL CARD */}
      <Sheet
        size={"small"}
        closable
        title={t("Enter Card Details")}
        cancelText={t("Cancel")}
        open={paymentState.manualCardModalVisible}
        okText={t("Process")}
        okDisabled={paymentState.selectedCustomerPaymentMethod ? false : !elementsComplete}
        // cancelDisabled={manualProcessing}
        backDropCancel={false}
        onOk={handleManualCardProcessing}
        stacked
        onCancel={async () => {
          await cancelManualCreditPaymentIntent(
            String(paymentState.transaction?.payment_intent_id),
            Number(paymentState.transaction?.id),
            true,
          );
          elements.getElement(CardNumberElement)?.clear();
          elements.getElement(CardExpiryElement)?.clear();
          elements.getElement(CardCvcElement)?.clear();
          setPaymentState(prevState => ({
            ...prevState,
            selectedCustomerPaymentMethod: undefined,
            manualCardModalVisible: !paymentState.manualCardModalVisible,
            showCustomerPaymentMethods: false,
            transaction: undefined,
            clientSecret: "",
            elementComplete: {
              cardCvc: false,
              cardExpiry: false,
              cardNumber: false,
            },
          }));
        }}
      >
        <div style={{ minHeight: "300px" }}>
          {/* Hide inputs when saved payment methods are displayed */}
          {!paymentState.showCustomerPaymentMethods && (
            <CheckoutForm
              onChange={handleCardSectionChange}
              onSubmit={() => setManualProcessing(!manualProcessing)}
              trigger={submitTrigger}
              transaction={paymentState.transaction}
              onError={(message: string) => {
                dispatch(showError(message));
                setManualProcessing(false);
                setSubmitTrigger(false);
              }}
              accountTransaction={true}
              onSuccess={() => {
                // Clean values
                dispatch(
                  showSuccess(t("Payment successful")),
                );
                setPaymentState(prevState => ({
                  ...prevState,
                  manualCardModalVisible: false,
                  transaction: undefined,
                  elementComplete: {
                    cardCvc: false,
                    cardExpiry: false,
                    cardNumber: false,
                  },
                }));
                setSubmitTrigger(false);
                props.onOk();
                props.closePaymentModal();
              }}
            />
          )}

          <div>
            {paymentState.showCustomerPaymentMethods && (
              <CreditCardCatalogue
                customerId={props.houseAccount.customer_id}
                creditCards={props.paymentMethods}
                selectedCard={
                  Number(paymentState.selectedCustomerPaymentMethod)
                    ? Number(paymentState.selectedCustomerPaymentMethod)
                    : undefined
                }
                setSelectedCard={paymentId =>
                  setPaymentState({
                    ...paymentState,
                    selectedCustomerPaymentMethod:
                      paymentId === paymentState.selectedCustomerPaymentMethod ? undefined : paymentId,
                  })
                }
                backButtonAction={() =>
                  setPaymentState(prevState => ({
                    ...prevState,
                    showCustomerPaymentMethods: false,
                    selectedCustomerPaymentMethod: undefined,
                  }))
                }
              />
            )}
          </div>

          {/* Toggles between checkout form and customer payment methods */}
          {props.paymentMethods?.length > 0 && !paymentState.showCustomerPaymentMethods && (
            <Button
              type="text"
              onClick={() => {
                setPaymentState(prevState => ({
                  ...prevState,
                  showCustomerPaymentMethods: true,
                  selectedCustomerPaymentMethod: undefined,
                  elementComplete: {
                    cardCvc: false,
                    cardExpiry: false,
                    cardNumber: false,
                  },
                }));
              }}
              className="mt-2 mr-auto"
            >
              <span>{t("Select Saved Card")}</span>
            </Button>
          )}
        </div>
      </Sheet>

      <Portal isMounted={paymentState.creditBookModalVisible}>
        <CreditBookModal
          onCancel={() => setPaymentState({ ...paymentState, creditBookModalVisible: false })}
          onOk={creditBook => {
            setPaymentState({
              ...paymentState,
              creditBookModalVisible: false,
              selectedCreditBook: creditBook,
            });
          }}
          loadCreditBooks
          customerId={props.houseAccount?.customer_id}
        />
      </Portal>
    </>
  );
}
